import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MdAddLocationAlt, MdFullscreen } from "react-icons/md";

import RouteMapFullScreen from "./RouteMapFullScreen";
import StopsMap, { MAP_BUTTON } from "./StopsMap";
import { EMBED_MAX_STOPS, embedUrl, googleMapsKey } from "./routeSheet";
import { mapsApi, mapStops, placesOf } from "./routeMapStops";
import { formatDuration, formatKm, loopPoints, routeLine } from "./runDistance";

const PANEL =
  "overflow-hidden rounded-[7px] border-[1px] border-ui-border bg-ui-surface print:hidden";
const NOTE =
  "m-[0px] border-t-[1px] border-ui-border px-[12px] py-[6px] text-[11px] text-ui-text-muted";
// Most of the window on a computer or tablet, a good part of a phone's.
const MAP_HEIGHT = "h-[55vh] min-h-[300px] sm:h-[58vh]";

// Why the sheet shows Google's embedded map instead, as its note says it.
const FALLBACK_NOTES = {
  unavailable:
    "Google's interactive map didn't load, so this simpler map has no stop numbers or full screen.",
  unplaced:
    "Google Maps couldn't place the stops, so this simpler map has no stop numbers or full screen.",
};

// Google's script loads with the page; if it isn't there yet, it's looked
// for again every half second for 10 seconds.
const API_WAIT_MS = 500;
const API_TRIES = 20;

// Google calls window.gm_authFailure when it refuses the key for the
// interactive map, which would then show only Google's error: the sheet
// shows the embedded map instead. Set when this loads, before any map is
// made; a handler already there still runs.
const refusal = { refused: false, listeners: new Set() };
if (typeof window !== "undefined") {
  const before = window.gm_authFailure;
  window.gm_authFailure = (...args) => {
    refusal.refused = true;
    refusal.listeners.forEach((listener) => listener());
    if (typeof before === "function") before(...args);
  };
}

// The Maps JavaScript API while it's usable here, else null.
const useMapsApi = () => {
  const [maps, setMaps] = useState(mapsApi);
  const [refused, setRefused] = useState(refusal.refused);
  useEffect(() => {
    const listener = () => setRefused(true);
    refusal.listeners.add(listener);
    return () => refusal.listeners.delete(listener);
  }, []);
  useEffect(() => {
    if (maps) return undefined;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      const found = mapsApi();
      if (found) setMaps(() => found);
      if (found || tries >= API_TRIES) clearInterval(timer);
    }, API_WAIT_MS);
    return () => clearInterval(timer);
  }, [maps]);
  return refused ? null : maps;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * The route sheet's map. On Google's interactive map (the Maps JavaScript
 * API the app loads): the run's road route in stop order (Directions, the
 * same request as the sheet's Drive line, in parts on a long run) and each
 * stop numbered as on the sheet, with a label (its reference, the
 * marketplace's order number staff know it by, and customer) where there's
 * room and a card with all of it (reference, Bolt's #, customer, address,
 * pieces, Navigate) when picked; the start and end location has its own
 * badge. Full screen shows the map over everything with the stops listed
 * beside it. When the interactive map isn't there (it didn't load, or
 * Google refused its key) or Google can't place the stops, it's Google's
 * embedded map as before, with a note saying so. `stops` are the sheet's
 * orders in delivery order, `start` the location the run starts and ends
 * at, `title` the run's name, `driveTotals` the sheet's Drive line (null
 * while it has none). On a run, `spots` marks where each recorded stop was
 * ({ [orderId]: stopSpot }) and `driver` where the driver is ({ place,
 * seenAt, self }: self on the driver's own phone), or null. On an open run,
 * `candidates` are the route's orders that could join it (orders, placed
 * with one more Directions request through them): small pins whose card has
 * Add to the run (`onAddStop(orderId)`), shown or hidden with their button;
 * a stop in `removableIds` has Take off the run (`onRemoveStop(orderId)`).
 * `busy` holds both while a change saves. Each card lists what's still to
 * deliver and the earlier deliveries with their photos.
 */
const RouteSheetMap = ({
  stops = [],
  start = null,
  title = "",
  driveTotals = null,
  spots = {},
  driver = null,
  candidates = [],
  onAddStop,
  removableIds = [],
  onRemoveStop,
  busy = false,
}) => {
  const maps = useMapsApi();
  const all = useMemo(() => mapStops(stops), [stops]);
  const startAddress = start?.address || null;
  const addresses = all.map((stop) => stop.address).filter(Boolean);
  const points = loopPoints(startAddress, addresses);
  const tripKey = points.join("\n");

  // Where Google placed each address (from every drive asked for while the
  // sheet is open), so moving a stop renumbers the pins straight away; the
  // route itself follows once Google answers for the new order.
  const known = useRef(new Map());
  // The drive for the stops in their order now: { key, state, path }
  // (state: "working", "done" or "failed").
  const [trip, setTrip] = useState({ key: "", state: "working", path: null });
  useEffect(() => {
    if (!maps || points.length < 2) return undefined;
    let cancelled = false;
    setTrip((current) =>
      current.key === tripKey
        ? current
        : { key: tripKey, state: "working", path: null },
    );
    routeLine(points, maps).then((line) => {
      if (line) {
        placesOf(points, line.legs).forEach((place, address) =>
          known.current.set(address, place),
        );
      }
      if (cancelled) return;
      setTrip({
        key: tripKey,
        state: line ? "done" : "failed",
        path: line?.path ?? null,
      });
    });
    return () => {
      cancelled = true;
    };
    // The points are the key's parts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maps, tripKey]);
  const tripState = trip.key === tripKey ? trip.state : "working";

  // The orders that could join the run, placed on the map with a drive
  // through them (its route isn't drawn): asked for once per set.
  const candidateStops = useMemo(
    () => mapStops(candidates).filter((order) => order.address),
    [candidates],
  );
  const candidatePoints = loopPoints(
    startAddress,
    candidateStops.map((order) => order.address),
  );
  const candidatesKey = candidatePoints.join("\n");
  const [, setCandidatesPlaced] = useState("");
  useEffect(() => {
    if (!maps || candidatePoints.length < 2) return undefined;
    let cancelled = false;
    routeLine(candidatePoints, maps).then((line) => {
      if (line) {
        placesOf(candidatePoints, line.legs).forEach((place, address) =>
          known.current.set(address, place),
        );
      }
      if (!cancelled) setCandidatesPlaced(candidatesKey);
    });
    return () => {
      cancelled = true;
    };
    // The points are the key's parts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maps, candidatesKey]);
  const [showCandidates, setShowCandidates] = useState(true);

  const [selection, setSelection] = useState(null);
  const seqRef = useRef(0);
  // Each pick is new (seq), so picking the same stop again still moves the
  // map to it.
  const select = useCallback((id, how = "map") => {
    seqRef.current += 1;
    setSelection(id == null ? null : { id, how, seq: seqRef.current });
  }, []);
  const [full, setFull] = useState(false);
  const fullRef = useRef(null);
  const closeFull = useCallback(() => {
    setFull(false);
    fullRef.current?.focus();
  }, []);

  if (!addresses.length) return null;

  const placeOf = (address) => (address && known.current.get(address)) || null;
  const placed = all.map((stop) => ({ ...stop, place: placeOf(stop.address) }));
  const routable = placed.filter((stop) => stop.address);
  const startPlace = placeOf(startAddress);
  // A location without an address isn't on the drive, nor on the map.
  const startMark = startAddress ? { ...start, place: startPlace } : null;
  const fallback = !maps
    ? "unavailable"
    : points.length < 2
      ? "single"
      : tripState === "failed" && routable.some((stop) => !stop.place)
        ? "unplaced"
        : null;

  if (fallback) {
    const src = embedUrl(googleMapsKey(), startAddress, addresses);
    if (!src) return null;
    return (
      <section className={PANEL}>
        <iframe
          title="Route map"
          src={src}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className={`block w-full border-0 ${MAP_HEIGHT}`}
        />
        {/* Google's box on the map is for its stops only, so it reads
            short of the Drive line on a longer run. */}
        {addresses.length > EMBED_MAX_STOPS && (
          <p className={NOTE}>
            The map can show only {EMBED_MAX_STOPS} stops, so its distance and
            time are for stops 1–{EMBED_MAX_STOPS}.{" "}
            {driveTotals
              ? `The Drive line is the full run, all ${addresses.length} stops.`
              : `The links and the list have all ${addresses.length}.`}
          </p>
        )}
        {FALLBACK_NOTES[fallback] && (
          <p className={NOTE}>{FALLBACK_NOTES[fallback]}</p>
        )}
      </section>
    );
  }

  const anyPlaced = Boolean(startPlace) || routable.some((stop) => stop.place);
  const status =
    tripState !== "working" ? null : anyPlaced ? "updating" : "placing";
  const path = tripState === "done" ? trip.path : null;
  // Fitted in again only when the places change, not the order.
  const fitKey = [
    startPlace ? startAddress : "",
    ...new Set(
      routable
        .filter((stop) => stop.place)
        .map((stop) => stop.address)
        .sort(),
    ),
  ].join("\n");
  const missing = all.length - routable.length;
  const summary = [
    plural(all.length, "stop"),
    missing > 0 && `${missing} not on the map`,
    startMark?.name && `from ${startMark.name} and back`,
    driveTotals &&
      `${formatKm(driveTotals.meters)} · ${formatDuration(driveTotals.seconds)}`,
  ]
    .filter(Boolean)
    .join(" · ");
  // Where each recorded stop was, numbered as its pin, with its address on
  // the map for the line drawn when it's far.
  const drops = placed
    .filter((stop) => spots[stop.id])
    .map((stop) => ({
      id: stop.id,
      number: stop.number,
      place: spots[stop.id].place,
      distance: spots[stop.id].distance,
      far: spots[stop.id].far,
      address: stop.place,
    }));
  const placedCandidates = showCandidates
    ? candidateStops
        .map((order) => ({ ...order, place: placeOf(order.address) }))
        .filter((order) => order.place)
    : [];
  const candidatesButton = candidateStops.length > 0 && (
    <button
      type="button"
      aria-pressed={showCandidates}
      aria-label={`${showCandidates ? "Hide" : "Show"} the ${plural(candidateStops.length, "order")} on this route not on a run`}
      title={`${plural(candidateStops.length, "order")} on this route not on a run: tap one to add it`}
      onClick={() => setShowCandidates((on) => !on)}
      className={`${MAP_BUTTON} ${showCandidates ? "border-brand text-brand" : ""}`}
    >
      <MdAddLocationAlt className="h-[16px] w-[16px]" />
      {candidateStops.length}
    </button>
  );
  const shared = {
    maps,
    start: startMark,
    stops: placed,
    path,
    fitKey,
    selection,
    onSelect: select,
    status,
    drops,
    driver,
    candidates: placedCandidates,
    onAdd: onAddStop,
    removableIds,
    onRemove: onRemoveStop,
    busy,
  };

  return (
    <section aria-label="Route map" className={PANEL}>
      <StopsMap
        {...shared}
        count={all.length}
        gestureHandling="cooperative"
        className={MAP_HEIGHT}
        tools={
          <>
            {candidatesButton}
            <button
              ref={fullRef}
              type="button"
              onClick={() => setFull(true)}
              className={MAP_BUTTON}
            >
              <MdFullscreen className="h-[18px] w-[18px]" />
              Full screen
            </button>
          </>
        }
      />
      {tripState === "failed" && (
        <p className={NOTE}>
          Google Maps couldn't work out the route for the stops in this order,
          so the map shows them without it.
        </p>
      )}
      {full && (
        <RouteMapFullScreen
          {...shared}
          tools={candidatesButton || null}
          title={title}
          summary={summary}
          onClose={closeFull}
        />
      )}
    </section>
  );
};

export default RouteSheetMap;
